import React, { useMemo } from 'react';
import { View, ImageBackground, StyleSheet } from 'react-native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { getCssRadialGlows, getGradientColors } from '@/constants/shared/theme';

interface GradientBackgroundProps {
  propertyImageUrl?: string | null;
  children: React.ReactNode;
}

const GradientFallback: React.FC<{ children: React.ReactNode }> = React.memo(
  ({ children }) => {
    const { mode } = useThemeColors();

    const gradientStyle = useMemo(() => {
      const cssGlows = getCssRadialGlows(mode);
      const { gradientStart, gradientEnd } = getGradientColors(mode);
      return {
        flex: 1,
        backgroundImage: `${cssGlows}, linear-gradient(180deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
      } as any;
    }, [mode]);

    return <View style={gradientStyle}>{children}</View>;
  },
);

const ImageVariant: React.FC<{
  imageUrl: string;
  children: React.ReactNode;
}> = React.memo(({ imageUrl, children }) => {
  const { colors } = useThemeColors();

  const imageOverlayStyle = useMemo(() => {
    const overlay = colors.gradient.imageOverlay;
    return {
      ...StyleSheet.absoluteFillObject,
      backgroundImage: `linear-gradient(180deg, ${overlay[0]} 0%, ${overlay[1]} 20%, ${overlay[2]} 40%, ${overlay[3]} 55%, ${overlay[4]} 70%, ${overlay[5]} 85%, ${overlay[6]} 100%)`,
    } as any;
  }, [colors.gradient.imageOverlay]);

  return (
    <View style={styles.container}>
      <ImageBackground
        source={{ uri: imageUrl }}
        style={StyleSheet.absoluteFill}
        resizeMode="cover"
      >
        <View style={imageOverlayStyle} />
      </ImageBackground>
      {children}
    </View>
  );
});

export const GradientBackground: React.FC<GradientBackgroundProps> = React.memo(
  ({ propertyImageUrl, children }) => {
    if (propertyImageUrl) {
      return <ImageVariant imageUrl={propertyImageUrl}>{children}</ImageVariant>;
    }
    return <GradientFallback>{children}</GradientFallback>;
  },
);

GradientBackground.displayName = 'GradientBackground';

export const RadialGlowOverlay = React.memo(() => {
  const { mode } = useThemeColors();
  const glowStyle = useMemo(
    () => ({
      ...StyleSheet.absoluteFillObject,
      backgroundImage: getCssRadialGlows(mode),
      pointerEvents: 'none',
    }) as any,
    [mode],
  );
  return <View style={glowStyle} />;
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
